import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { 
  detectLanguageFromIP, 
  getCachedLanguage, 
  cacheDetectedLanguage,
  getUserLocationData 
} from './utils/locationDetection';

i18n
  // Load translation files from public/locales
  .use(HttpBackend)
  // Detect user language from browser or localStorage
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    // Default language
    fallbackLng: 'en',
    
    // Available languages
    supportedLngs: ['en', 'id'],
    
    // Debug mode (disable in production)
    debug: false,
    
    // Namespace for translations
    ns: ['translation'],
    defaultNS: 'translation',
    
    // Detection options
    detection: {
      // '?lng=' comes first so a URL can pin a language. That is what makes the
      // hreflang annotations in useSEO legitimate: each language needs its own
      // crawlable URL, otherwise both versions share one address and search
      // engines have nothing to point at.
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lng',
      // Don't auto-cache on init - we'll cache manually after IP detection
      caches: [],
      // localStorage key
      lookupLocalStorage: 'i18nextLng',
    },
    
    // Backend options for loading translation files
    backend: {
      // Path to translation files
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    // Interpolation options
    interpolation: {
      // React already escapes values
      escapeValue: false,
    },
    
    // React options
    react: {
      // Wait for translations to load before rendering
      useSuspense: true,
    },
  });

/**
 * Keeps <html lang> in step with the active language.
 *
 * It was hardcoded to "en" in index.html and never changed, which misstates the
 * page language to screen readers, to browser translation prompts, and to search
 * engines — for every Indonesian visitor, on every page.
 */
function syncDocumentLanguage(language: string) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language.slice(0, 2).toLowerCase();
}

syncDocumentLanguage(i18n.language || 'en');
i18n.on('languageChanged', syncDocumentLanguage);

/** True when the URL explicitly pins a language, e.g. /pricing?lng=id */
export function hasExplicitLanguageParam(): boolean {
  if (typeof window === 'undefined') return false;
  const requested = new URLSearchParams(window.location.search).get('lng');
  return requested === 'en' || requested === 'id';
}

// Initialize IP-based language detection (runs after initial setup)
export async function initializeIPLanguageDetection() {
  try {
    // An explicit ?lng= in the URL outranks geography. Without this guard the
    // IP lookup would overwrite it a second after load, which would both break
    // shared links and make the hreflang URLs serve the wrong language.
    if (hasExplicitLanguageParam()) {
      return;
    }

    // Check if user has manually set a language preference
    const isManualSelection = localStorage.getItem('languageManuallySelected') === 'true';
    
    // Always fetch location data (for currency/pricing features)
    await getUserLocationData();
    
    // Only apply language if user hasn't manually selected one
    if (!isManualSelection) {
      let detectedLang = getCachedLanguage();
      
      if (!detectedLang) {
        detectedLang = await detectLanguageFromIP();
        cacheDetectedLanguage(detectedLang);
      }
      
      if (detectedLang) {
        await i18n.changeLanguage(detectedLang);
        // Store the detected language but mark it as auto-detected (not manual)
        localStorage.setItem('i18nextLng', detectedLang);
      }
    }
    
  } catch (error) {
    console.warn('Failed to initialize IP language detection:', error);
  }
}

// Auto-initialize IP detection when module loads (non-blocking)
if (typeof window !== 'undefined') {
  // Run after a short delay to not block initial page load
  setTimeout(() => {
    initializeIPLanguageDetection();
  }, 1000);
}

export default i18n;
